import React, { useState, useContext, useEffect, useRef } from 'react';
import { ThemeProvider } from 'styled-components';

import ErrorContext from '../../../_common/Controller/errorContext';
import { FilePickerProps } from '../../FilePicker';

import { disableDOMDragDefaultEvents, enableDOMDragDefaultEvents } from '../../../../helpers/dom';
import setFocus from '../../../../helpers/dom/setFocus';

import FilesErrors from '../FilesErrors/FilesErrors';
import FilesList from '../FilesList/FilesList';

import Icon from '../../../Icon/Icon';

import { getUpdateFiles, fileAccepted, getLabel } from '../../helpers/filePickerHelpers';

import {
  FilePickerActions,
  FilePickerDraggable,
  FilePickerHandler,
  FilePickerHandlerLabel,
  FilePickerHandlerText,
  FilePickerInput,
  FilePickerList,
  FilePickerLoadingIcon,
  FilePickerUploadIcon,
  StyledFilePicker,
} from '../../styles';

function CoreFilePicker(props: FilePickerProps) {
  const {
    className,
    customError,
    disabled,
    id,
    isCell,
    isInvalid,
    isLoading,
    onDelete,
    size,
    style,
    value,
    setMultiple,
    'data-testid': dataTestId,
    ...inputProps
  } = props;

  const [dragging, setDragging] = useState(false);
  const [hasMimeTypeError, setHasMimeTypeError] = useState<boolean>(false);
  const errorContext = useContext(ErrorContext);

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (setMultiple) {
      setMultiple(inputProps.multiple);
    }

    disableDOMDragDefaultEvents();
    if (inputProps.autoFocus) {
      setFocus(inputRef);
    }

    return () => {
      enableDOMDragDefaultEvents();
    };
  }, [inputProps.autoFocus, inputProps.multiple, setMultiple]);

  const onBlur = () => {
    if (inputProps.onBlur) {
      inputProps.onBlur(value);
    }
  };

  const onFocus = () => {
    if (inputProps.onFocus) {
      inputProps.onFocus(value);
    }
  };

  const handleUpdate = newFiles => {
    const acceptedFiles = getUpdateFiles(value, newFiles, inputProps);
    if (acceptedFiles.length > 0) {
      inputProps.onChange && inputProps.onChange(acceptedFiles);
    }
  };

  const onChange = e => {
    const { files } = e.target;
    setHasMimeTypeError(false);
    handleUpdate(files);
  };

  const onDrop = e => {
    const { files } = e.dataTransfer;

    setDragging(false);

    const newFiles = Array.from(files);

    const hasMimetypeError = newFiles.some(file => !fileAccepted(file, props.accept));

    setHasMimeTypeError(hasMimetypeError);
    handleUpdate(newFiles);

    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleDelete = file => {
    setHasMimeTypeError(false);
    onDelete(file);
  };

  const showList = Boolean(value?.length || Boolean(inputProps.accept && hasMimeTypeError) || Boolean(customError));

  const contextLevelError = errorContext.errorAutoInvalidate && errorContext.hasError;
  const localLevelError = isInvalid || hasMimeTypeError || !!customError;

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        fpCell: isCell,
        fpDisabled: disabled,
        fpDragging: dragging,
        fpInvalid: contextLevelError || localLevelError,
        fpFilled: showList,
        fpLoading: isLoading,
        fpSize: size,
      })}
    >
      <StyledFilePicker data-testid={dataTestId}>
        <FilePickerDraggable
          htmlFor={id}
          onDragOver={() => setDragging(true)}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <FilePickerInput
            {...inputProps}
            id={id}
            ref={inputRef}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            type="file"
            value=""
            aria-invalid={contextLevelError || localLevelError}
            aria-errormessage={contextLevelError ? errorContext.errorMessageId : undefined}
            data-testid={dataTestId && `${dataTestId}-input`}
            disabled={disabled}
          />

          <FilePickerActions>
            <FilePickerHandler>
              <FilePickerHandlerText>
                Drop or&nbsp;
                <FilePickerHandlerLabel htmlFor={id} data-testid={dataTestId && `${dataTestId}-handle`}>
                  {getLabel(value, inputProps)}
                </FilePickerHandlerLabel>
              </FilePickerHandlerText>
            </FilePickerHandler>

            <FilePickerLoadingIcon>
              <Icon type="loading" isSpinner meaning="loading" aria-busy="true" size="xxl" />
            </FilePickerLoadingIcon>

            <FilePickerUploadIcon>
              <Icon type="upload" aria-hidden="true" size="xxl" />
            </FilePickerUploadIcon>
          </FilePickerActions>

          {showList && (
            <FilePickerList data-testid={dataTestId && `${dataTestId}-files-list`}>
              <FilesErrors accept={inputProps.accept} hasMimeTypeError={hasMimeTypeError} customError={customError} />
              <FilesList files={value} handleDelete={handleDelete} disabled={disabled} />
            </FilePickerList>
          )}
        </FilePickerDraggable>
      </StyledFilePicker>
    </ThemeProvider>
  );
}

CoreFilePicker.defaultProps = {
  name: null,
  multiple: false,
  onFocus: () => {},
  onChange: () => {},
  onBlur: () => {},
  onDelete: () => {},
  accept: undefined,
  size: 'large',
  isCell: false,
  disabled: false,
  isInvalid: false,
  autoFocus: false,
  customError: undefined,
  'data-testid': undefined,
};

export default CoreFilePicker;
